import { useMemo, useState } from 'react';
import { useBiometrics } from 'hooks/useBiometrics';
import { ReadingRow } from 'components/ProgressTab/WidgetDetail/WidgetReadingList';
import { DateRangePickerProps } from 'components/DateRangePicker/DateRangePicker';
import {
  compareDates,
  getDateByWeeksOffset,
  longFormatDate,
  shortChartLabel,
  parseLocalDateString,
  toLocalYmd,
} from 'utils/dateUtils';
import { useBiometricDownload } from 'hooks/useBiometricDownload';

/** Today at local midnight — the end of the current (most recent) 7-day window. */
const currentWeekEnd = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

/** end - 6 days (inclusive 7-day window start). */
const weekStartFor = (end: Date): Date => {
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  return start;
};

/** Builds the "Apr 27 - May 03, 2026" range label. */
const buildRangeLabel = (start: Date, end: Date): string => {
  const startLabel = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(start);
  const endLabel = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(end);
  return `${startLabel} - ${endLabel}`;
};

export interface UseWeightDetailsResult {
  dateRange: DateRangePickerProps;
  dates: string[];
  weights: (number | null)[];
  latestValue: number | string;
  unit?: string;
  readings: ReadingRow[];
  isLoading: boolean;
  isError: boolean;
  download: () => void;
  isDownloading: boolean;
  isDownloadError: boolean;
}

export const useWeightDetails = (userId?: number): UseWeightDetailsResult => {
  const [end, setEnd] = useState<Date>(currentWeekEnd);
  const start = useMemo(() => weekStartFor(end), [end]);

  // Past Readings (all available weight data for the user).
  const { weight } = useBiometrics(userId, {});

  const units = weight.data?.units;

  // All readings for this member, oldest → newest.
  const allChrono = useMemo(() => {
    const points = weight.data?.data ?? [];
    return [...points].sort(
      (a, b) => parseLocalDateString(a.date).getTime() - parseLocalDateString(b.date).getTime(),
    );
  }, [weight.data]);

  // Readings that fall inside the currently-selected 7-day window.
  const windowChrono = useMemo(
    () =>
      allChrono.filter(
        (p) =>
          compareDates(parseLocalDateString(p.date), start) >= 0 &&
          compareDates(parseLocalDateString(p.date), end) <= 0,
      ),
    [allChrono, start, end],
  );

  // Chart + "latest value" are scoped to the currently-selected 7-day window.
  const chart = useMemo(() => {
    const recentFirst = [...windowChrono].reverse();
    return {
      dates: windowChrono.map((p) => shortChartLabel(p.date)),
      weights: windowChrono.map((p) => p.value ?? null),
      latestValue: recentFirst[0]?.value ?? '',
      unit: units,
    };
  }, [windowChrono, units]);

  // Past Readings list shows ALL readings returned (full one-year history),
  // most-recent-first — independent of the chart's weekly window.
  const readings = useMemo<ReadingRow[]>(
    () =>
      [...allChrono]
        .reverse()
        .filter((p): p is typeof p & { value: number } => p.value != null)
        .map((p) => ({
          id: String(p.id),
          value: p.value,
          unit: units ?? '',
          dateLabel: longFormatDate(p.timestamp),
        })),
    [allChrono, units],
  );

  // Overall data boundaries (exact, since we have every reading).
  const earliestReading = allChrono[0] ? parseLocalDateString(allChrono[0].date) : undefined;
  const latestReading = allChrono[allChrono.length - 1]
    ? parseLocalDateString(allChrono[allChrono.length - 1].date)
    : undefined;

  const isCurrentWeek = compareDates(end, currentWeekEnd()) >= 0;

  // Disable Previous if there is no reading strictly before the window start.
  const disablePrevious =
    !weight.isLoading && (!earliestReading || compareDates(earliestReading, start) >= 0);

  // Disable Next at/after the current week, or if no reading exists after the window end.
  const disableNext =
    isCurrentWeek ||
    (!weight.isLoading && (!latestReading || compareDates(latestReading, end) <= 0));

  const dateRange: DateRangePickerProps = {
    label: buildRangeLabel(start, end),
    onPrevious: () => setEnd((prev) => getDateByWeeksOffset(-1, prev)),
    onNext: () => setEnd((prev) => getDateByWeeksOffset(1, prev)),
    disablePrevious,
    disableNext,
  };

  // CSV download of the member's full weight history.
  const {
    download,
    isDownloading,
    isError: isDownloadError,
  } = useBiometricDownload('weight', userId ?? 0);

  return {
    dateRange,
    dates: chart.dates,
    weights: chart.weights,
    latestValue: chart.latestValue,
    unit: chart.unit,
    readings,
    isLoading: weight.isLoading,
    isError: weight.isError,
    download,
    isDownloading,
    isDownloadError,
  };
};
